import React, { useState, useEffect, useCallback } from 'react';
import { X, Send, Mail, Phone, Calendar, MapPin, CheckCircle, ChevronDown, Search, Loader, Building, User, Home } from 'lucide-react';
import './ContactModal.css';

const STRUCTURE_TYPES = [
  { value: 'micro-parti', label: 'Micro-parti' },
  { value: 'mandataire-physique', label: 'Mandataire financier personne physique' },
  { value: 'association-financement', label: 'Association de financement' },
  { value: 'societe', label: 'Société' },
  { value: 'personne-physique', label: 'Personne physique autre' },
];

function ContactModal({ isOpen, onClose, customTokenData = null }) {
  // Si customTokenData est fourni, on démarre directement à l'étape 2
  const [step, setStep] = useState(customTokenData ? 2 : 1);
  const [formData, setFormData] = useState({
    email: customTokenData?.email || '',
    telephone: '',
    scrutinId: '',
    scrutinNom: customTokenData?.scrutinNom || '',
    scrutinType: '',
    commune: customTokenData?.commune || '',
    communePopulation: customTokenData?.communePopulation || null,
  });
  
  // Prix personnalisé depuis le token (prioritaire sur le calcul)
  const customPricing = customTokenData ? {
    type: 'fixed',
    price: parseFloat(customTokenData.priceEuros),
    label: `${customTokenData.priceEuros} €`,
    isCustomToken: true,
  } : null;
  const [billingData, setBillingData] = useState({
    structureType: '',
    nom: '',
    prenom: '',
    microPartiNom: '',
    nomStructure: '', // Pour société et association
    adresse: '',
    codePostal: '',
    ville: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  // États pour les scrutins
  const [scrutins, setScrutins] = useState([]);
  const [isLoadingScrutins, setIsLoadingScrutins] = useState(false);
  const [scrutinDropdownOpen, setScrutinDropdownOpen] = useState(false);
  
  // États pour les communes
  const [communes, setCommunes] = useState([]);
  const [isLoadingCommunes, setIsLoadingCommunes] = useState(false);
  const [communeSearch, setCommuneSearch] = useState('');
  const [communeDropdownOpen, setCommuneDropdownOpen] = useState(false);
  
  // États pour l'autocomplétion d'adresse
  const [adresses, setAdresses] = useState([]);
  const [isLoadingAdresses, setIsLoadingAdresses] = useState(false);
  const [adresseSearch, setAdresseSearch] = useState('');
  const [adresseDropdownOpen, setAdresseDropdownOpen] = useState(false);

  // Grille de prix depuis MongoDB
  const [pricingGrid, setPricingGrid] = useState(null);

  // Charger la grille de prix au montage
  useEffect(() => {
    const fetchPricingGrid = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
        const response = await fetch(`${backendUrl}/demo-leads/pricing-grid`);
        if (response.ok) {
          const data = await response.json();
          if (data.result && data.data) {
            setPricingGrid(data.data);
          }
        }
      } catch (error) {
        console.warn('[ContactModal] Impossible de charger la grille de prix, utilisation du fallback');
      }
    };
    fetchPricingGrid();
  }, []);

  // Réinitialiser les données quand le modal s'ouvre avec un token personnalisé
  useEffect(() => {
    if (isOpen && customTokenData) {
      setStep(2);
      setFormData({
        email: customTokenData.email || '',
        telephone: '',
        scrutinId: '',
        scrutinNom: customTokenData.scrutinNom || '',
        scrutinType: '',
        commune: customTokenData.commune || '',
        communePopulation: customTokenData.communePopulation || null,
      });
    }
  }, [isOpen, customTokenData]);

  // Charger les scrutins au montage
  useEffect(() => {
    const fetchScrutins = async () => {
      setIsLoadingScrutins(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
        const response = await fetch(`${backendUrl}/nextScrutins/public`);
        const data = await response.json();
        if (data?.result && data.scrutins) {
          setScrutins(data.scrutins);
        }
      } catch (error) {
        console.error('Erreur récupération scrutins:', error);
      } finally {
        setIsLoadingScrutins(false);
      }
    };
    
    if (isOpen) {
      fetchScrutins();
    }
  }, [isOpen]);

  // Rechercher les communes (API gouv.fr) - par nom ou code postal
  const searchCommunes = useCallback(async (search) => {
    if (search.length < 2) {
      setCommunes([]);
      return;
    }
    
    setIsLoadingCommunes(true);
    try {
      // Détecte si c'est un code postal (que des chiffres)
      const isPostalCode = /^\d+$/.test(search);
      
      let data = [];
      
      if (isPostalCode) {
        // Recherche par code postal
        const url = `https://geo.api.gouv.fr/communes?codePostal=${encodeURIComponent(search)}&limit=20&fields=nom,code,codesPostaux,departement,population&type=commune-actuelle,arrondissement-municipal`;
        const response = await fetch(url);
        data = await response.json();
      } else {
        // Recherche par nom avec boost population
        const url = `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(search)}&limit=20&fields=nom,code,codesPostaux,departement,population&type=commune-actuelle,arrondissement-municipal&boost=population`;
        const response = await fetch(url);
        data = await response.json();
        
        // Si pas de résultats, retenter sans les articles (le, la, les, l')
        if (data.length === 0) {
          const searchWithoutArticle = search.replace(/^(le |la |les |l')/i, '').trim();
          if (searchWithoutArticle !== search && searchWithoutArticle.length >= 2) {
            const url2 = `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(searchWithoutArticle)}&limit=20&fields=nom,code,codesPostaux,departement,population&type=commune-actuelle,arrondissement-municipal&boost=population`;
            const response2 = await fetch(url2);
            data = await response2.json();
          }
        }
      }
      
      setCommunes(data);
    } catch (error) {
      console.error('Erreur recherche communes:', error);
    } finally {
      setIsLoadingCommunes(false);
    }
  }, []);

  // Debounce de la recherche de communes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (communeSearch.length >= 2) {
        searchCommunes(communeSearch);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [communeSearch, searchCommunes]);

  // Rechercher les adresses (API adresse.data.gouv.fr)
  const searchAdresses = useCallback(async (search) => {
    if (search.length < 5) {
      setAdresses([]);
      return;
    }
    
    setIsLoadingAdresses(true);
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(search)}&limit=8`
      );
      const data = await response.json();
      setAdresses(data.features || []);
    } catch (error) {
      console.error('Erreur recherche adresses:', error);
    } finally {
      setIsLoadingAdresses(false);
    }
  }, []);

  // Debounce de la recherche d'adresses
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (adresseSearch.length >= 5) {
        searchAdresses(adresseSearch);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [adresseSearch, searchAdresses]);

  // Sélection d'une adresse
  const handleAdresseSelect = (feature) => {
    const props = feature.properties;
    setBillingData(prev => ({
      ...prev,
      adresse: props.name || '',
      codePostal: props.postcode || '',
      ville: props.city || '',
    }));
    setAdresseDropdownOpen(false);
    setAdresseSearch('');
  };

  // Vérifier si le scrutin est de type Municipales
  const isMunicipales = formData.scrutinType?.toLowerCase().includes('municipale');

  // Calculer le prix en fonction de la population (via grille MongoDB ou fallback)
  const calculatePrice = (population) => {
    if (!population) return null;

    // Si grille MongoDB disponible, l'utiliser
    if (pricingGrid?.populationTiers?.length > 0) {
      const sortedTiers = [...pricingGrid.populationTiers].sort((a, b) => a.minPop - b.minPop);
      
      for (const tier of sortedTiers) {
        const minOk = population >= tier.minPop;
        const maxOk = tier.maxPop === null || population < tier.maxPop;
        
        if (minOk && maxOk) {
          if (tier.priceCents === null) {
            return { type: 'custom', label: tier.label || 'Sur demande' };
          }
          const priceEuros = tier.priceCents / 100;
          return { 
            type: 'fixed', 
            price: priceEuros, 
            label: tier.label || `${priceEuros} €` 
          };
        }
      }
      // Aucun tier trouvé
      return { type: 'custom', label: 'Sur demande' };
    }

    // Fallback hardcodé si grille non disponible (janvier 2026)
    if (population >= 50000) return { type: 'custom', label: 'Sur demande' };
    if (population < 20000) return { type: 'fixed', price: 199, label: '199 €' };
    if (population < 30000) return { type: 'fixed', price: 299, label: '299 €' };
    if (population < 40000) return { type: 'fixed', price: 399, label: '399 €' };
    return { type: 'fixed', price: 499, label: '499 €' }; // 40k-50k
  };

  // Utiliser le prix du token personnalisé si disponible, sinon calculer
  const pricing = customPricing || calculatePrice(formData.communePopulation);

  const handleScrutinSelect = (scrutin) => {
    setFormData(prev => ({
      ...prev,
      scrutinId: scrutin._id,
      scrutinNom: scrutin.nom,
      scrutinType: scrutin.typeScrutin,
      commune: '', // Reset commune si on change de scrutin
    }));
    setScrutinDropdownOpen(false);
  };

  const handleCommuneSelect = (commune) => {
    const populationStr = commune.population ? ` • ${commune.population.toLocaleString('fr-FR')} hab.` : '';
    setFormData(prev => ({
      ...prev,
      commune: `${commune.nom} (${commune.departement?.code || ''})${populationStr}`,
      communePopulation: commune.population || null,
    }));
    setCommuneDropdownOpen(false);
    setCommuneSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Toujours envoyer une alerte Slack au clic sur "Calculer la tarification"
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
      const payload = {
        email: formData.email,
        telephone: formData.telephone,
        zone: `${formData.scrutinNom}${formData.commune ? ` - ${formData.commune}` : ''}`,
        scrutinId: formData.scrutinId,
        scrutinNom: formData.scrutinNom,
        scrutinType: formData.scrutinType,
        commune: formData.commune,
        communePopulation: formData.communePopulation,
        prix: pricing?.price,
        source: 'landing',
      };
      await fetch(`${backendUrl}/demo-leads/procumatcher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('Erreur envoi Slack:', error);
    } finally {
      setIsSubmitting(false);
    }
    
    // Si tarification personnalisée (> 100k habitants), on termine ici
    if (pricing?.type === 'custom') {
      setIsSubmitted(true);
      return;
    }
    
    // Sinon, passer à l'étape 2
    setStep(2);
  };

  const handleClose = () => {
    setFormData({ email: '', telephone: '', scrutinId: '', scrutinNom: '', scrutinType: '', commune: '', communePopulation: null });
    setBillingData({ structureType: '', nom: '', prenom: '', microPartiNom: '', nomStructure: '', adresse: '', codePostal: '', ville: '' });
    setStep(1);
    setIsSubmitted(false);
    setScrutinDropdownOpen(false);
    setCommuneDropdownOpen(false);
    setCommuneSearch('');
    onClose();
  };

  // Formater l'affichage des tours d'un scrutin
  const formatScrutinLabel = (scrutin) => {
    const toursInfo = scrutin.tours?.map(t => {
      const date = t.date ? new Date(t.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '?';
      return `${t.label}: ${date}`;
    }).join(' / ') || '';
    return `${scrutin.nom}${toursInfo ? ` (${toursInfo})` : ''}`;
  };

  const isFormValid = formData.email && formData.scrutinId && (!isMunicipales || formData.commune);
  
  const needsNomStructure = ['association-financement', 'societe'].includes(billingData.structureType);
  const needsPrenom = billingData.structureType !== 'association-financement'; // Société a besoin de prénom
  const isBillingFormValid = billingData.structureType && 
    billingData.nom && 
    (!needsPrenom || billingData.prenom) && 
    (!needsNomStructure || billingData.nomStructure) &&
    billingData.adresse && 
    billingData.codePostal && 
    billingData.ville &&
    (billingData.structureType !== 'micro-parti' || billingData.microPartiNom);

  // Soumission finale - redirige vers Stripe Checkout
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
      
      const payload = {
        // Données initiales
        email: formData.email,
        telephone: formData.telephone,
        scrutinNom: formData.scrutinNom,
        scrutinType: formData.scrutinType,
        commune: formData.commune,
        communePopulation: formData.communePopulation,
        // Données de facturation
        structureType: billingData.structureType,
        nom: billingData.nom,
        prenom: billingData.prenom,
        microPartiNom: billingData.microPartiNom,
        nomStructure: billingData.nomStructure,
        adresse: billingData.adresse,
        codePostal: billingData.codePostal,
        ville: billingData.ville,
        source: customTokenData ? 'custom-link' : 'landing',
        // Token personnalisé (pour les liens d'achat personnalisés)
        customToken: customTokenData?.token || null,
      };
      
      // Créer la session Stripe et récupérer l'URL
      const response = await fetch(`${backendUrl}/demo-leads/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.result) {
        throw new Error(data.message || 'Erreur serveur');
      }
      
      // Rediriger vers Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Erreur Stripe:', error);
      alert(`Erreur: ${error.message || 'Impossible de créer le paiement. Vérifiez que le backend est lancé.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-modal-overlay" onClick={handleClose}>
      <div className={`contact-modal ${step === 2 ? 'contact-modal--large' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="contact-modal__header">
          <div>
            <h2 className="contact-modal__title">Obtenir ProcuMatcher</h2>
            <p className="contact-modal__subtitle">Calculez votre tarification personnalisée</p>
          </div>
          <button className="contact-modal__close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>

        {isSubmitted ? (
          <div className="contact-modal__success">
            <div className="contact-modal__success-icon">
              <CheckCircle size={32} />
            </div>
            <h3>Demande envoyée !</h3>
            <p>Notre équipe vous contactera rapidement pour activer votre ProcuMatcher.</p>
            <button className="contact-modal__btn" onClick={handleClose}>
              Fermer
            </button>
          </div>
        ) : step === 2 ? (
          /* Étape 2 : Formulaire de création */
          <div className="contact-modal__step2">
            <div className="contact-modal__price-summary">
              <div className="contact-modal__price">
                <span className="contact-modal__price-value">{pricing?.price}€ <span className="contact-modal__price-ttc">TTC</span></span>
              </div>
              <p className="contact-modal__price-info">
                Forfait unique pour {formData.communePopulation?.toLocaleString('fr-FR')} habitants
              </p>
              <p className="contact-modal__price-detail">
                Accès complet, qu'il y ait ou non second tour.
              </p>
            </div>

            <form onSubmit={handleFinalSubmit} className="contact-modal__form">
              <h4 className="contact-modal__section-title">Créer mon ProcuMatcher de campagne</h4>
              
              {/* Entité de facturation */}
              <div className="contact-modal__field">
                <label>Entité de facturation *</label>
                <div className="contact-modal__radio-group">
                  {STRUCTURE_TYPES.map((type) => (
                    <label key={type.value} className={`contact-modal__radio ${billingData.structureType === type.value ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="structureType"
                        value={type.value}
                        checked={billingData.structureType === type.value}
                        onChange={(e) => setBillingData(prev => ({ ...prev, structureType: e.target.value }))}
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Nom du micro-parti (si micro-parti) */}
              {billingData.structureType === 'micro-parti' && (
                <div className="contact-modal__field">
                  <label>Nom du micro-parti *</label>
                  <div className="contact-modal__input-wrapper">
                    <Building size={18} />
                    <input
                      type="text"
                      required
                      value={billingData.microPartiNom}
                      onChange={(e) => setBillingData(prev => ({ ...prev, microPartiNom: e.target.value }))}
                      placeholder="Nom du micro-parti"
                    />
                  </div>
                </div>
              )}

              {/* Nom association OU Nom société + Prénom/Nom selon le type */}
              {billingData.structureType === 'association-financement' ? (
                <div className="contact-modal__field">
                  <label>Nom de l'association de financement *</label>
                  <div className="contact-modal__input-wrapper">
                    <Building size={18} />
                    <input
                      type="text"
                      required
                      value={billingData.nomStructure || ''}
                      onChange={(e) => setBillingData(prev => ({ ...prev, nomStructure: e.target.value, nom: e.target.value, prenom: '-' }))}
                      placeholder="Association de financement..."
                    />
                  </div>
                </div>
              ) : billingData.structureType === 'societe' ? (
                <>
                  <div className="contact-modal__field">
                    <label>Nom de la société *</label>
                    <div className="contact-modal__input-wrapper">
                      <Building size={18} />
                      <input
                        type="text"
                        required
                        value={billingData.nomStructure || ''}
                        onChange={(e) => setBillingData(prev => ({ ...prev, nomStructure: e.target.value }))}
                        placeholder="Nom de la société..."
                      />
                    </div>
                  </div>
                  <div className="contact-modal__field-row">
                    <div className="contact-modal__field">
                      <label>Prénom du représentant *</label>
                      <div className="contact-modal__input-wrapper">
                        <User size={18} />
                        <input
                          type="text"
                          required
                          value={billingData.prenom}
                          onChange={(e) => setBillingData(prev => ({ ...prev, prenom: e.target.value }))}
                          placeholder="Jean"
                        />
                      </div>
                    </div>
                    <div className="contact-modal__field">
                      <label>Nom du représentant *</label>
                      <div className="contact-modal__input-wrapper">
                        <User size={18} />
                        <input
                          type="text"
                          required
                          value={billingData.nom}
                          onChange={(e) => setBillingData(prev => ({ ...prev, nom: e.target.value }))}
                          placeholder="Dupont"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="contact-modal__field-row">
                  <div className="contact-modal__field">
                    <label>Prénom *</label>
                    <div className="contact-modal__input-wrapper">
                      <User size={18} />
                      <input
                        type="text"
                        required
                        value={billingData.prenom}
                        onChange={(e) => setBillingData(prev => ({ ...prev, prenom: e.target.value }))}
                        placeholder="Jean"
                      />
                    </div>
                  </div>
                  <div className="contact-modal__field">
                    <label>Nom *</label>
                    <div className="contact-modal__input-wrapper">
                      <User size={18} />
                      <input
                        type="text"
                        required
                        value={billingData.nom}
                        onChange={(e) => setBillingData(prev => ({ ...prev, nom: e.target.value }))}
                        placeholder="Dupont"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Adresse avec autocomplétion */}
              <div className="contact-modal__field">
                <label>Adresse postale *</label>
                <div className="contact-modal__dropdown">
                  <div className="contact-modal__input-wrapper">
                    <Home size={18} />
                    <input
                      type="text"
                      required
                      value={billingData.adresse || adresseSearch}
                      onChange={(e) => {
                        setAdresseSearch(e.target.value);
                        setBillingData(prev => ({ ...prev, adresse: '', codePostal: '', ville: '' }));
                        setAdresseDropdownOpen(true);
                      }}
                      onFocus={() => adresseSearch.length >= 5 && setAdresseDropdownOpen(true)}
                      placeholder="Commencez à taper votre adresse..."
                    />
                    {isLoadingAdresses && <Loader size={18} className="spinner" />}
                  </div>
                  
                  {adresseDropdownOpen && adresses.length > 0 && (
                    <div className="contact-modal__dropdown-menu">
                      {adresses.map((feature, index) => (
                        <button
                          key={index}
                          type="button"
                          className="contact-modal__dropdown-item"
                          onClick={() => handleAdresseSelect(feature)}
                        >
                          <span className="commune-name">{feature.properties.name}</span>
                          <span className="commune-dept">{feature.properties.postcode} {feature.properties.city}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Code postal et Ville */}
              <div className="contact-modal__field-row">
                <div className="contact-modal__field" style={{ flex: '0 0 120px' }}>
                  <label>Code postal *</label>
                  <input
                    type="text"
                    required
                    value={billingData.codePostal}
                    onChange={(e) => setBillingData(prev => ({ ...prev, codePostal: e.target.value }))}
                    placeholder="75001"
                    className="contact-modal__input-simple"
                  />
                </div>
                <div className="contact-modal__field" style={{ flex: 1 }}>
                  <label>Ville *</label>
                  <input
                    type="text"
                    required
                    value={billingData.ville}
                    onChange={(e) => setBillingData(prev => ({ ...prev, ville: e.target.value }))}
                    placeholder="Paris"
                    className="contact-modal__input-simple"
                  />
                </div>
              </div>

              {/* Champs pré-remplis (lecture seule) */}
              <div className="contact-modal__readonly-section">
                <div className="contact-modal__field">
                  <label>Email</label>
                  <div className="contact-modal__input-wrapper readonly">
                    <Mail size={18} />
                    <input type="email" value={formData.email} readOnly />
                  </div>
                </div>
                <div className="contact-modal__field">
                  <label>Téléphone</label>
                  <div className="contact-modal__input-wrapper readonly">
                    <Phone size={18} />
                    <input type="tel" value={formData.telephone || 'Non renseigné'} readOnly />
                  </div>
                </div>
                <div className="contact-modal__field">
                  <label>Commune</label>
                  <div className="contact-modal__input-wrapper readonly">
                    <MapPin size={18} />
                    <input type="text" value={formData.commune} readOnly />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !isBillingFormValid}
                className="contact-modal__submit"
              >
                {isSubmitting ? (
                  <>
                    <span className="contact-modal__spinner" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Créer mon ProcuMatcher
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="contact-modal__form">
            {/* Email */}
            <div className="contact-modal__field">
              <label>Email *</label>
              <div className="contact-modal__input-wrapper">
                <Mail size={18} />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="vous@exemple.fr"
                />
              </div>
            </div>

            {/* Téléphone */}
            <div className="contact-modal__field">
              <label>Téléphone</label>
              <div className="contact-modal__input-wrapper">
                <Phone size={18} />
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
                  placeholder="06 12 34 56 78"
                />
              </div>
            </div>

            {/* Election concernée */}
            <div className="contact-modal__field">
              <label>Élection concernée *</label>
              <div className="contact-modal__dropdown">
                <button
                  type="button"
                  className="contact-modal__dropdown-trigger"
                  onClick={() => setScrutinDropdownOpen(!scrutinDropdownOpen)}
                >
                  <Calendar size={18} />
                  <span className={formData.scrutinNom ? '' : 'placeholder'}>
                    {formData.scrutinNom || 'Sélectionner une élection'}
                  </span>
                  {isLoadingScrutins ? <Loader size={18} className="spinner" /> : <ChevronDown size={18} />}
                </button>
                
                {scrutinDropdownOpen && (
                  <div className="contact-modal__dropdown-menu">
                    {scrutins.length === 0 ? (
                      <div className="contact-modal__dropdown-empty">
                        {isLoadingScrutins ? 'Chargement...' : 'Aucune élection disponible'}
                      </div>
                    ) : (
                      scrutins.map((scrutin) => (
                        <button
                          key={scrutin._id}
                          type="button"
                          className={`contact-modal__dropdown-item ${formData.scrutinId === scrutin._id ? 'selected' : ''}`}
                          onClick={() => handleScrutinSelect(scrutin)}
                        >
                          {formatScrutinLabel(scrutin)}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Commune (si Municipales) */}
            {isMunicipales && (
              <div className="contact-modal__field">
                <label>Commune concernée *</label>
                <div className="contact-modal__dropdown">
                  <div className="contact-modal__input-wrapper">
                    <MapPin size={18} />
                    <input
                      type="text"
                      value={formData.commune || communeSearch}
                      onChange={(e) => {
                        setCommuneSearch(e.target.value);
                        setFormData(prev => ({ ...prev, commune: '' }));
                        setCommuneDropdownOpen(true);
                      }}
                      onFocus={() => setCommuneDropdownOpen(true)}
                      placeholder="Rechercher une commune..."
                    />
                    {isLoadingCommunes && <Loader size={18} className="spinner" />}
                  </div>
                  
                  {communeDropdownOpen && communes.length > 0 && (
                    <div className="contact-modal__dropdown-menu">
                      {communes.map((commune) => (
                        <button
                          key={commune.code}
                          type="button"
                          className="contact-modal__dropdown-item"
                          onClick={() => handleCommuneSelect(commune)}
                        >
                          <span className="commune-name">{commune.nom}</span>
                          <span className="commune-dept">
                            {commune.departement?.nom} ({commune.codesPostaux?.[0] || ''})
                            {commune.population && ` • ${commune.population.toLocaleString('fr-FR')} hab.`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              className="contact-modal__submit"
            >
              {isSubmitting ? (
                <>
                  <span className="contact-modal__spinner" />
                  Calcul en cours...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Calculer la tarification
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ContactModal;
